import RAPIER from '@dimforge/rapier3d-compat'

import { quatToRobloxOrientation } from '../engine/quat'
import type { ObjectBodyRegistry } from './objectBodyRegistry'
import {
  CARRY_CONTACT_SKIN,
  CARRY_IDENTITY_ROTATION,
  GROUND_TOUCH_MARGIN,
} from './physicsConstants'
import { composeYaw, normalizeAngleDelta } from './poseMath'
import type { PreviewWorld } from '../types'

/**
 * 動く台（回転・移動するプラットフォーム）へのアバター追従サブシステム。
 *
 * createAvatarController から「乗っている台の追従」を切り出したもの。
 * 前ステップで接地していた台の姿勢スナップショット（carriedPlatform）は
 * ファクトリ内部に隠蔽し、本体には差分計算（computeCarryDelta）・適用
 * （applyCarry）・基準記録（rememberGroundPlatform）・クリア（clear。
 * 死亡・乗車・reset 用）だけを公開する。
 */
/** computeCarryDelta が返す追従差分。applyCarry へそのまま渡す。 */
export interface CarryDelta {
  x: number
  y: number
  z: number
  deltaYaw: number
  /** 追従元の台の id（applyCarry のスイープで障害物から除外する）。 */
  platformId: string | undefined
}

export interface PlatformCarry {
  /**
   * 乗っている台の Y 回転・平行移動の差分から、アバターへ適用すべき
   * 移動量と向きの回転量を計算する。
   */
  computeCarryDelta(): CarryDelta
  /** computeCarryDelta の移動量を、障害物の手前でクランプして適用する。 */
  applyCarry(carry: CarryDelta): void
  /** ステップ末に次ステップの追従基準（乗っている台の姿勢）を記録する。 */
  rememberGroundPlatform(): void
  /**
   * 追従基準のヨーだけを台の現在値へ取り直す（位置・サイズの基準は保持する）。
   * アバターの向きを台の回転へ直接整合させた側（syncSpawnFacing）が、次の
   * ステップの carry で同じ回転を二重適用されないために呼ぶ。位置基準を
   * 保持するのは、同時に動かされた台の移動 carry を消さないため。
   */
  rebaseYaw(platformId: string): void
  /** 追従スナップショットを破棄する（死亡・乗車・reset 時）。 */
  clear(): void
}

/**
 * 台追従サブシステムを生成する。
 *
 * @param deps.physicsWorld rapier の物理 World（carry スイープの実行元）。
 * @param deps.world 見た目・状態の真実源。台の現在姿勢の参照元。
 * @param deps.avatarBody アバターの剛体（現在位置の参照・テレポート適用先）。
 * @param deps.registry collider handle → objectId の逆引き。
 * @param deps.getAvatarCollider アバターのコライダー。本体所有の参照へ構築順に依存せずアクセスするため getter で受け取る。
 * @param deps.castGroundRay 足元の接地相手を返すレイキャスト（physicsController が所有）。
 * @param deps.isGrounded 直近ステップで接地していたか（本体の歩行状態）。
 * @param deps.isSwimming 直近ステップで遊泳中か（本体の歩行状態）。
 * @param deps.isDead 死亡演出中か（本体の歩行状態）。
 */
export const createPlatformCarry = (deps: {
  physicsWorld: RAPIER.World
  world: PreviewWorld
  avatarBody: RAPIER.RigidBody
  registry: Pick<ObjectBodyRegistry, 'objectIdOf'>
  getAvatarCollider: () => RAPIER.Collider
  castGroundRay: (margin: number) => RAPIER.Collider | null
  isGrounded: () => boolean
  isSwimming: () => boolean
  isDead: () => boolean
}): PlatformCarry => {
  const {
    physicsWorld,
    world,
    avatarBody,
    registry,
    getAvatarCollider,
    castGroundRay,
    isGrounded,
    isSwimming,
    isDead,
  } = deps
  // 乗っている台の追従用: 前ステップで接地していた台の姿勢スナップショット。
  // 台が回転・移動したらアバターを同じだけ動かす（computeCarryDelta）。
  let carriedPlatform: {
    id: string
    position: { x: number; y: number; z: number }
    yaw: number
    /** 記録時の台の縦サイズ。サイズ変更（拡縮）による天面の上下追従に使う。 */
    sizeY: number
  } | null = null

  /** 台の現在ヨー（基準向き ∘ 実行時ヨー の Y 成分、ラジアン）を返す。 */
  const platformYawOf = (state: {
    orientation: { x: number; y: number; z: number; w: number }
    rotationY: number
  }): number =>
    (quatToRobloxOrientation(composeYaw(state.orientation, state.rotationY)).y *
      Math.PI) /
    180

  /**
   * 乗っている台（前ステップ末に記録した接地相手）の Y 回転・平行移動の
   * 差分から、アバターへ適用すべき移動量（台の旧中心まわりの公転 +
   * 平行移動）と向きの回転量を計算する。
   *
   * プログラム（movement_setYOrientationTo / moveSteps 等）で台を回す・
   * 動かすと、collider の姿勢は同期されるが上に立つアバターはその場に
   * 残ってしまう（台だけ回って自分は回らない）ための追従処理。
   * 対象は Y 軸ヨー + 平行移動のみ（X/Z 軸の傾き変化には追従しない。
   * ミッションの移動ブロックはヨーと平行移動しか変えないため）。
   * 返した差分は applyCarry で壁手前までクランプして適用する。
   */
  const computeCarryDelta = (): CarryDelta => {
    // 空中・遊泳・死亡では追従しない。通常は前ステップ末の
    // rememberGroundPlatform が同条件で snapshot をクリア済みのため
    // 冗長だが、呼び出し順の変更に備えた防御として明示しておく。
    if (!carriedPlatform || !isGrounded() || isSwimming() || isDead()) {
      carriedPlatform = null
      return { x: 0, y: 0, z: 0, deltaYaw: 0, platformId: undefined }
    }
    const state = world.getState().get(carriedPlatform.id)
    // 台が消えた・すり抜け化（CanCollide=false）した場合は追従をやめる
    // （非衝突の台はもう「乗って」いない。次ステップ以降は落下に任せる）。
    if (!state || !state.canCollide) {
      carriedPlatform = null
      return { x: 0, y: 0, z: 0, deltaYaw: 0, platformId: undefined }
    }
    const deltaYaw = normalizeAngleDelta(
      platformYawOf(state) - carriedPlatform.yaw,
    )
    // 台の旧中心まわりの相対位置を deltaYaw 回し、新中心へ平行移動した
    // 位置と現在位置の差分を返す。
    const current = avatarBody.translation()
    const relX = current.x - carriedPlatform.position.x
    const relZ = current.z - carriedPlatform.position.z
    const cos = Math.cos(deltaYaw)
    const sin = Math.sin(deltaYaw)
    // 「大きさを変える」ブロックの拡縮は中心固定で行われるため、中心位置
    // (position.y)は変わらず天面だけが size.y の半分ずつ上下する。乗っている
    // アバターを天面の上下分だけ押し上げ/下げ、拡大時にブロックへ埋まるのを防ぐ。
    const topRise = (state.size.y - carriedPlatform.sizeY) / 2
    return {
      x: state.position.x + relX * cos + relZ * sin - current.x,
      y: state.position.y - carriedPlatform.position.y + topRise,
      z: state.position.z - relX * sin + relZ * cos - current.z,
      deltaYaw,
      platformId: carriedPlatform.id,
    }
  }

  /**
   * computeCarryDelta の移動量をアバターへ適用する。
   *
   * KCC の desired への合算は、台の移動直後の大きな移動量で接触解決が
   * 誤って移動を阻む（障害物がないのに autostep が発動して止まる）事例が
   * あるため使わない。代わりにアバターのカプセル全体を carry ベクトルに
   * 沿って castShape で sweep し、追従元の台以外の障害物（壁・天井・
   * 低い段差など）の手前でクランプしたテレポートとして適用する。
   */
  const applyCarry = (carry: CarryDelta): void => {
    if (carry.x === 0 && carry.y === 0 && carry.z === 0) {
      return
    }
    const avatarCollider = getAvatarCollider()
    const platformId = carry.platformId
    const current = avatarBody.translation()
    // stopAtPenetration=false: すでに接触している面（接地中の足元など）は
    // めり込みが深まらない限り無視する。追従元の台自身は障害物ではない
    // （台の上に立ったまま運ばれる）ため predicate で除外する。
    const hit = physicsWorld.castShape(
      current,
      CARRY_IDENTITY_ROTATION,
      carry,
      avatarCollider.shape,
      CARRY_CONTACT_SKIN,
      1,
      false,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      avatarCollider,
      undefined,
      (collider) => registry.objectIdOf(collider.handle) !== platformId,
    )
    const scale = hit ? Math.max(0, hit.time_of_impact) : 1
    avatarBody.setTranslation(
      {
        x: current.x + carry.x * scale,
        y: current.y + carry.y * scale,
        z: current.z + carry.z * scale,
      },
      true,
    )
  }

  /**
   * ステップ末（移動・接地確定後）に、次ステップの追従基準となる
   * 「乗っている台」の姿勢（id・位置・ヨー）を記録する。
   * 空中・遊泳中・台から降りたとき・死亡中は追従しない（クリア）。
   */
  const rememberGroundPlatform = (): void => {
    if (!isGrounded() || isSwimming() || isDead()) {
      carriedPlatform = null
      return
    }
    const collider = castGroundRay(GROUND_TOUCH_MARGIN)
    const objectId = collider ? registry.objectIdOf(collider.handle) : undefined
    const state =
      objectId !== undefined ? world.getState().get(objectId) : undefined
    if (objectId === undefined || !state) {
      carriedPlatform = null
      return
    }
    carriedPlatform = {
      id: objectId,
      position: { ...state.position },
      yaw: platformYawOf(state),
      sizeY: state.size.y,
    }
  }

  const rebaseYaw = (platformId: string): void => {
    if (!carriedPlatform || carriedPlatform.id !== platformId) {
      return
    }
    const state = world.getState().get(platformId)
    if (state) {
      carriedPlatform = { ...carriedPlatform, yaw: platformYawOf(state) }
    }
  }

  const clear = (): void => {
    carriedPlatform = null
  }

  return {
    computeCarryDelta,
    applyCarry,
    rememberGroundPlatform,
    rebaseYaw,
    clear,
  }
}
